import { useEffect, useState } from "react";
import countriesData from "@resideo/blueprint-react/dist/components/CountryStateSelect/countries.json";
import { stateLoaders } from "@resideo/blueprint-react/dist/generated/stateLoaders.js";

export interface CountryStateOption {
  code: string;
  name: string;
}

type BlueprintRegion = { iso2: string; name: string };
type StateLoader = () => Promise<{ default: BlueprintRegion[] }>;

export const COUNTRIES: CountryStateOption[] = (
  countriesData as BlueprintRegion[]
)
  .map((country) => ({ code: country.iso2, name: country.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

const statesCache = new Map<string, CountryStateOption[]>();

export async function loadCountryStates(
  countryCode: string,
): Promise<CountryStateOption[]> {
  const cached = statesCache.get(countryCode);
  if (cached) return cached;

  const loader = (stateLoaders as Record<string, StateLoader | undefined>)[
    countryCode
  ];
  if (!loader) return [];

  const loaded = await loader();
  const states = (loaded.default ?? [])
    .map((state) => ({ code: state.iso2, name: state.name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  statesCache.set(countryCode, states);
  return states;
}

export interface CountryStatesResult {
  states: CountryStateOption[];
  /** Distinguishes "still fetching" from "this country has no subdivisions". */
  isLoading: boolean;
}

/** States/provinces for a country, loaded on demand from Blueprint's generated data. */
export function useCountryStates(countryCode: string): CountryStatesResult {
  const [loadedCountry, setLoadedCountry] = useState(() => ({
    countryCode,
    states: statesCache.get(countryCode) ?? [],
  }));
  const [isLoading, setIsLoading] = useState(
    () => Boolean(countryCode) && !statesCache.has(countryCode),
  );

  useEffect(() => {
    let cancelled = false;

    if (!countryCode) {
      setLoadedCountry({ countryCode: "", states: [] });
      setIsLoading(false);
      return;
    }

    const cached = statesCache.get(countryCode);
    if (cached) {
      setLoadedCountry({ countryCode, states: cached });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    void loadCountryStates(countryCode)
      .then((loaded) => {
        if (!cancelled) setLoadedCountry({ countryCode, states: loaded });
      })
      .catch(() => {
        if (!cancelled) setLoadedCountry({ countryCode, states: [] });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [countryCode]);

  const matchesCountry = loadedCountry.countryCode === countryCode;

  return {
    states: matchesCountry ? loadedCountry.states : [],
    isLoading: Boolean(countryCode) && (!matchesCountry || isLoading),
  };
}
